import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import PropTypes, { shape } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useApi from 'hooks/useApi';
import { loadDocument } from 'redux/actions/resultActions';
import {
  getDocumentDetails,
  getReadDocument,
  setNavigationPane,
  refreshPath,
  getDocumentPath,
} from 'redux/actions/documentActions';

import { LabelInput, LabelError } from 'shared/components/Labels';
import { Button, ButtonLabelIconOnly, ButtonIcon } from 'shared/components/Buttons';
import { TextField, Switch } from 'shared/components/form/Inputs';
import MainSpinner from 'shared/components/MainSpinner';
import TextArea from 'shared/components/textarea';
import { colors } from 'shared/defaultTheme';
import AlertModal from 'shared/components/AlertModal';
import MultipleSelectSearch from 'shared/components/MultipleSelectSearch';
import ConfirmModal from 'shared/components/ConfirmModal';

import { ReactComponent as DownloadIcon } from 'assets/icon-download.svg';
import { ReactComponent as MapMarkerIcon } from 'assets/icon-map-marker.svg';
import { ReactComponent as ConversationIcon } from 'assets/icon-conversation.svg';
import useNotification from 'hooks/useNotification';
import { setShowTypeNotifications } from 'redux/actions/notificationActions';
import {
  FormWrapper,
  GroupInputWrapper,
  DocumentDropdownSearch,
  CustomInputFile,
  MultipleSelectSearchDocument,
  CustomInputComunicationName,
} from './styles';

const DocumentForm = ({
  client,
  showError,
  errorMessage,
  uploadedError,
  color,
  onErrorLogoUploading,
  onToggleError,
  onUpdateDocument,
  onSubmit,
  onCancel,
  disbleDocument,
  states,
  tags,
  newDocs,
  newState,
  newTags,
  newPublic,
  loadingCreate,
  loadingPublicDocument,
  loadingUpdate,
  setCurrentColor,
  contacts: contactsProp,
  disableButton,
  setErrorMessage,
}) => {
  const { customAreaApi } = useApi();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isCustomer, mail, authToken, admins } = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((sta) => sta.topBar);
  const documentState = useSelector((state) => state.document);
  const { t } = useTranslation();
  const newPermissionContacts = useRef(null);
  const [i18Tags, setI18Tags] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showConfirmExistFile, setShowConfirmExistFile] = useState(false);
  const [showConfirmUnPublic, setShowConfirmUnPublic] = useState(false);
  const [confirmMessages, setConfirmMessages] = useState([]);
  const { getMassiveById, getIndividualById, handleSelectNotificationReview } = useNotification();
  const [notificationData, setNotificationData] = useState();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      ...documentState.documentDtails,
      public: documentState.documentDtails?.readers?.length > 0,
      permissionContacts: documentState.documentDtails?.readers || [],
    },
  });
  const newDDocs = newDocs;
  const newSState = newState;
  const newTTags = newTags;
  const newPPublic = newPublic;
  newDDocs.current = watch('docs', '');
  newSState.current = watch('state', '');
  newTTags.current = watch('tags', []);
  newPPublic.current = watch('public', false);
  newPermissionContacts.current = watch('permissionContacts', []);

  useEffect(() => {
    setContacts(contactsProp);
  }, [contactsProp]);

  const [{ loading: loadingDownload }, getDownload] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [, getCheckItemExists] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const handleCheckItemExists = async () => {
    const { clientId, clientNavisionId, pathId } = documentState.readDocument;
    const {
      data: { data },
    } = await getCheckItemExists({
      url: `${
        window.API_V1
      }/cli/items/checkitemexists/${clientNavisionId}/${clientId}/${encodeURIComponent(
        mail
      )}?parentPathId=${pathId}`,
      data: [newDDocs.current.name],
    });
    return data;
  };

  useEffect(() => {
    (async () => {
      try {
        let notifyData;
        if (documentState?.documentDtails?.idNotify && documentState?.documentDtails?.clientId) {
          dispatch(setShowTypeNotifications(false));
          notifyData = await getIndividualById({
            currentClientId: documentState.documentDtails.clientId,
            notificationId: documentState.documentDtails.idNotify,
          });
          setNotificationData(notifyData);
        }
        if (documentState?.documentDtails?.idGeneralNotify) {
          dispatch(setShowTypeNotifications(true));
          notifyData = await getMassiveById(documentState.documentDtails.idGeneralNotify);
          setNotificationData(notifyData);
        }
      } catch (err) {
        console.error('Error occured when fetching notification details');
      }
    })();
  }, [documentState]);

  useEffect(async () => {
    if (newDDocs.current !== '' && newDDocs.current) {
      const existItems = await handleCheckItemExists();
      if (existItems.rename) {
        let newNameFile = [];

        const name = existItems.namesFinal[newDDocs.current.name];
        if (name) {
          newNameFile = [
            ...newNameFile,
            t('documents_exits_file_messages', {
              interpolation: { escapeValue: false },
              oldName: newDDocs.current.name,
              newName: name,
              route: documentState.pathName,
            }),
          ];
        }
        setConfirmMessages(newNameFile);
        setShowConfirmExistFile(true);
      }
    }
  }, [newDDocs.current]);

  useEffect(() => {
    if (newSState.current) {
      setCurrentColor(newSState.current);
    }
  }, [newSState.current]);

  useEffect(() => {
    if (newPermissionContacts?.current?.length === 0 && newPPublic?.current === true) {
      setShowConfirmUnPublic(true);
    }
  }, [newPermissionContacts.current]);

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  useEffect(() => {
    let newI18Tags = [];
    tags.forEach((tag) => {
      newI18Tags = [...newI18Tags, { name: getNameSelectedLanguage(tag.i18List), id: tag.id }];
    });
    setI18Tags(newI18Tags);
  }, [selectedDefaultLanguage]);

  const onDownload = async () => {
    try {
      const { clientNavisionId, clientId, id, name } = documentState.documentDtails;
      const { data } = await getDownload({
        url: `${window.API_V1}/cli/items/${clientNavisionId}/${clientId}/${encodeURIComponent(
          mail
        )}/download/${id}`,
      });
      if (data) {
        const url = `${window.REACT_APP_SERVER_URL}${
          window.API_V1
        }/cli/items/${clientNavisionId}/${clientId}/${encodeURIComponent(mail)}/download/${id}`;
        const result = await fetch(url, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const blob = await result.blob();
        const tempLink = window.document.createElement('a');
        tempLink.href = window.URL.createObjectURL(blob);
        tempLink.setAttribute('download', name);
        tempLink.click();
      }
    } catch (error) {
      setErrorMessage(t('custom_field_operation_error_message'));
      onToggleError();
    }
  };

  const onOkExistFile = () => {
    setShowConfirmExistFile(false);
  };

  const onCancelExistFile = () => {
    setShowConfirmExistFile(false);
  };

  const onOkUnPublic = () => {
    setValue('public', false);
    setShowConfirmUnPublic(false);
  };

  const onCancelAction = () => {
    setShowConfirmUnPublic(false);
  };

  const goToNotificationDetails = () => {
    if (notificationData) {
      dispatch(getReadDocument(null));
      dispatch(getDocumentPath(null));
      handleSelectNotificationReview(notificationData);
      history.push(`/notificacion_details`, { isClientNotify: false });
    }
  };

  return (
    <>
      <FormWrapper className="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Row className="form-container">
          <Col md="12">
            <Row className="group-input-container">
              <Col>
                <GroupInputWrapper>
                  <LabelInput label={t('general_name')} color={colors.black} />
                  <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: { value: false, message: t('general_required_field') },
                    }}
                    render={({ field }) => (
                      <TextField
                        value={(newDocs.current && newDocs.current.name) || field.value}
                        onChange={field.onChange}
                        error={errors.name !== undefined}
                        borderRadius={2}
                        inputClassName="w-100"
                        buttonClassName=""
                        withButtonClear={false}
                        isReadOnly={!documentState.documentDtails}
                      />
                    )}
                  />
                  {errors.name && <LabelError label={errors.name.message} />}
                </GroupInputWrapper>
              </Col>
              <Col>
                {documentState &&
                  documentState.documentDtails &&
                  !documentState.documentDtails.isFile && (
                    <GroupInputWrapper>
                      <LabelInput label={t('general_text_description')} color={colors.black} />
                      <Controller
                        name="description"
                        control={control}
                        defaultValue=""
                        rules={{
                          required: { value: false, message: t('general_required_field') },
                        }}
                        render={({ field }) => (
                          <TextArea
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.description !== undefined}
                            row="20"
                            cols="30"
                          />
                        )}
                      />
                      {errors.description && <LabelError label={errors.description.message} />}
                    </GroupInputWrapper>
                  )}
                {documentState &&
                documentState.documentDtails &&
                !documentState.documentDtails.isFile ? null : (
                  <GroupInputWrapper>
                    <LabelInput label={t('general_text_attach')} color={colors.black} />
                    <Controller
                      name="docs"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: {
                          value: !documentState.documentDtails,
                          message: t('general_required_field'),
                        },
                      }}
                      render={({ field }) => (
                        <CustomInputFile
                          onChangeFile={field.onChange}
                          onError={onErrorLogoUploading}
                          error={errors.docs !== undefined}
                          disabled={disbleDocument}
                          updateDocument={!!documentState.documentDtails}
                          onUpdateDocument={onUpdateDocument}
                          name={
                            documentState.documentDtails?.name
                              ? documentState.documentDtails?.name
                              : ''
                          }
                        />
                      )}
                    />
                    {errors.docs && <LabelError label={errors.docs.message} />}
                    {uploadedError && <LabelError label={uploadedError} />}
                  </GroupInputWrapper>
                )}
              </Col>
            </Row>
            {documentState.documentDtails && (
              <Row className="group-input-container">
                <Col>
                  <GroupInputWrapper
                    className={
                      documentState &&
                      documentState.documentDtails &&
                      !documentState.documentDtails.isFile
                        ? 'folder-view'
                        : ''
                    }
                  >
                    <LabelInput label={t('general_client')} color={colors.black} />
                    <TextField
                      value={client}
                      onChange={() => null}
                      borderRadius={2}
                      inputClassName="w-100"
                      buttonClassName=""
                      withButtonClear={false}
                      isReadOnly
                    />
                  </GroupInputWrapper>
                </Col>
                <Col>
                  <GroupInputWrapper
                    className={
                      documentState &&
                      documentState.documentDtails &&
                      !documentState.documentDtails.isFile
                        ? 'folder-view2'
                        : ''
                    }
                  >
                    <LabelInput label={t('general_location')} color={colors.black} />
                    <div className="general-location-container">
                      <TextField
                        value={documentState.pathName}
                        onChange={() => null}
                        borderRadius={2}
                        inputClassName="w-100"
                        buttonClassName=""
                        withButtonClear={false}
                        isReadOnly
                      />
                      <ButtonIcon
                        title={t('general_location')}
                        id="general-location"
                        Icon={() => (
                          <MapMarkerIcon fill={colors.black} height="21px" width="21.12px" />
                        )}
                        className="general-location-icon"
                        onClick={() => {
                          dispatch(loadDocument(true));
                          dispatch(getDocumentDetails(null));
                          dispatch(getReadDocument(null));
                          dispatch(refreshPath(true));
                          dispatch(setNavigationPane(true));
                          history.push('/documentations');
                        }}
                      />
                    </div>
                  </GroupInputWrapper>
                </Col>
              </Row>
            )}
            {isCustomer && (
              <Row className="group-input-container">
                {documentState &&
                documentState.documentDtails &&
                !documentState.documentDtails.isFile ? null : (
                  <Col>
                    <GroupInputWrapper>
                      <LabelInput label={t('new_document_text_version')} color={colors.black} />
                      <Controller
                        name="version"
                        control={control}
                        defaultValue=""
                        rules={{
                          required: { value: false, message: t('general_required_field') },
                          pattern: {
                            value: /^([0-9]+)[.][0-9]{0,2}$/,
                            message: t('general_invalid_number'),
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            value={field.value.toLocaleString('en-US', {
                              maximumFractionDigits: 4,
                              minimumFractionDigits: 1,
                            })}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.version !== undefined}
                            borderRadius={2}
                            type="number"
                            inputClassName="w-100"
                            buttonClassName=""
                            withButtonClear={false}
                          />
                        )}
                      />
                      {errors.version && <LabelError label={errors.version.message} />}
                    </GroupInputWrapper>
                  </Col>
                )}
                {notificationData &&
                  documentState &&
                  documentState.documentDtails &&
                  !documentState.documentDtails.isFile && (
                    <Col sm={12} md={6}>
                      <CustomInputComunicationName className="folder-view4">
                        <span className="title">{t('document_to_comunication_label')}</span>
                        <div
                          className="input"
                          onClick={goToNotificationDetails}
                          onKeyDown={goToNotificationDetails}
                          role="button"
                          tabIndex="0"
                        >
                          <span className="text">{notificationData?.name || ''}</span>
                          <span className="icon">
                            <ConversationIcon fill={colors.black} className="communication-icon" />
                          </span>
                        </div>
                      </CustomInputComunicationName>
                    </Col>
                  )}
                {documentState &&
                documentState.documentDtails &&
                !documentState.documentDtails.isFile ? null : (
                  <Col>
                    <GroupInputWrapper>
                      <LabelInput label={t('general_state')} color={colors.black} />
                      <Controller
                        name="state"
                        control={control}
                        defaultValue=""
                        rules={{
                          required: { value: false, message: t('general_required_field') },
                        }}
                        render={({ field }) => (
                          <DocumentDropdownSearch
                            id="word-document-dropdown-search"
                            options={states.map((item) => ({
                              name: getNameSelectedLanguage(item.i18List),
                              value: item.id,
                              color: item.color,
                            }))}
                            titleNotFound={t('state_not_found')}
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            error={errors.state !== undefined}
                            isColor
                            color={color}
                          />
                        )}
                      />
                      {errors.state && <LabelError label={errors.state.message} />}
                    </GroupInputWrapper>
                  </Col>
                )}
              </Row>
            )}
            <Row className="group-input-container">
              <Col>
                {documentState &&
                documentState.documentDtails &&
                !documentState.documentDtails.isFile ? null : (
                  <GroupInputWrapper>
                    <LabelInput label={t('general_text_description')} color={colors.black} />
                    <Controller
                      name="description"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: { value: false, message: t('general_required_field') },
                      }}
                      render={({ field }) => (
                        <TextArea
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.description !== undefined}
                          row="20"
                          cols="30"
                        />
                      )}
                    />
                    {errors.description && <LabelError label={errors.description.message} />}
                  </GroupInputWrapper>
                )}
              </Col>
              <Col>
                {documentState &&
                documentState.documentDtails &&
                !documentState.documentDtails.isFile
                  ? null
                  : isCustomer && (
                      <GroupInputWrapper>
                        <LabelInput label={t('general_text_clasification')} color={colors.black} />
                        <Controller
                          name="tags"
                          control={control}
                          rules={{
                            required: { value: false, message: t('general_required_field') },
                          }}
                          defaultValue={[]}
                          render={({ field }) => (
                            <MultipleSelectSearchDocument
                              dataSource={i18Tags}
                              fields={{ text: 'name', value: 'id' }}
                              defaultValue={field.value.length > 0 ? field.value : undefined}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              error={errors.tags !== undefined}
                              titleNotFound={t('tag_not_found')}
                            />
                          )}
                        />
                        {errors.tags && <LabelError label={errors.tags.message} />}
                      </GroupInputWrapper>
                    )}
                {isCustomer && (
                  <>
                    <GroupInputWrapper
                      className={
                        documentState &&
                        documentState.documentDtails &&
                        !documentState.documentDtails.isFile
                          ? 'folder-view3 pt-4 mb-3'
                          : 'pt-4 mb-3'
                      }
                    >
                      <Controller
                        name="public"
                        control={control}
                        rules={{
                          required: { value: false, message: t('general_required_field') },
                        }}
                        defaultValue={false}
                        render={({ field }) => (
                          <Switch
                            id="public"
                            classNameMain="flex-row-reverse justify-content-end"
                            className="label-input ml-2"
                            label={
                              documentState &&
                              documentState.documentDtails &&
                              !documentState.documentDtails.isFile
                                ? t(field.value ? 'general_shared_folder' : 'general_share')
                                : t(field.value ? 'general_shared' : 'general_share')
                            }
                            onChange={(checked) => {
                              if (!checked && documentState.documentDtails?.readers.length > 0) {
                                setShowConfirmUnPublic(true);
                              } else {
                                field.onChange(checked);
                              }
                            }}
                            error={errors.public !== undefined}
                            value={!field.value}
                            color={field.value ? colors.pastelGreen : colors.venetianRed}
                            textColor={colors.black}
                          />
                        )}
                      />
                      {errors.public && <LabelError label={errors.public.message} />}
                    </GroupInputWrapper>
                    <GroupInputWrapper hidden={!newPPublic.current}>
                      <LabelInput label={t('document_contact_permissions')} color={colors.black} />
                      <Controller
                        name="permissionContacts"
                        control={control}
                        rules={{
                          required: {
                            value: newPPublic.current,
                            message: t('general_required_field'),
                          },
                        }}
                        defaultValue={[]}
                        render={({ field }) => (
                          <MultipleSelectSearch
                            dataSource={contacts}
                            fields={{ text: 'nameComplete', value: 'mail' }}
                            defaultValue={field.value.length > 0 ? field.value : undefined}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.permissionContacts !== undefined}
                          />
                        )}
                      />
                      {errors.permissionContacts && (
                        <LabelError label={errors.permissionContacts.message} />
                      )}
                    </GroupInputWrapper>
                  </>
                )}
                {!isCustomer && (
                  <GroupInputWrapper>
                    <LabelInput
                      label={t('notification_select_advisor_text')}
                      color={colors.black}
                    />
                    <Controller
                      name="permissionContacts"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t('general_required_field'),
                        },
                      }}
                      defaultValue={[]}
                      render={({ field }) => (
                        <>
                          <MultipleSelectSearch
                            dataSource={admins.filter((x) => x.active)}
                            fields={{ text: 'nameComplete', value: 'mail' }}
                            defaultValue={field.value.length > 0 ? field.value : undefined}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.permissionContacts !== undefined}
                          />
                        </>
                      )}
                    />
                    {errors.permissionContacts && (
                      <LabelError label={errors.permissionContacts.message} />
                    )}
                  </GroupInputWrapper>
                )}
              </Col>
            </Row>
            {notificationData &&
              documentState &&
              documentState.documentDtails &&
              documentState.documentDtails.isFile && (
                <Row className="group-input-container">
                  <Col sm={12} md={6}>
                    <CustomInputComunicationName>
                      <span className="title">{t('document_to_comunication_label')}</span>
                      <div
                        className="input"
                        onClick={goToNotificationDetails}
                        onKeyDown={goToNotificationDetails}
                        role="button"
                        tabIndex="0"
                      >
                        <span className="text">{notificationData?.name || ''}</span>
                        <span className="icon">
                          <ConversationIcon fill={colors.black} className="communication-icon" />
                        </span>
                      </div>
                    </CustomInputComunicationName>
                  </Col>
                </Row>
              )}
            <Row className="group-input-container align-items-end my-5 btn-action">
              <Col className="btn-action-container">
                {documentState.documentDtails ? (
                  <>
                    {isCustomer ? (
                      <Button
                        className="home__btn text-uppercase"
                        type="submit"
                        label={t('general_save_change')}
                        color="primary"
                        disabled={disableButton}
                      />
                    ) : (
                      <>
                        {mail === documentState?.documentDtails.createdBy && (
                          <Button
                            className="home__btn text-uppercase"
                            type="submit"
                            label={t('general_save_change')}
                            color="primary"
                            disabled={disableButton}
                          />
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <Button
                    className="home__btn text-uppercase"
                    type="submit"
                    label={t('general_add')}
                    color="primary"
                    disabled={disableButton}
                  />
                )}
                <Button
                  className="home__btn text-uppercase mr-4"
                  type="button"
                  label={t('general_button_cancel')}
                  color="secondary"
                  onClick={() => onCancel()}
                />
                {documentState.documentDtails && documentState.documentDtails.isFile && (
                  <ButtonLabelIconOnly
                    id="add-icon-client"
                    text={t('documents_download_text')}
                    type="button"
                    Icon={() => <DownloadIcon />}
                    onClick={() => onDownload()}
                  />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </FormWrapper>
      {(loadingCreate || loadingUpdate || loadingDownload || loadingPublicDocument) && (
        <MainSpinner />
      )}
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={errorMessage}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      <ConfirmModal
        isOpen={showConfirmExistFile}
        toggle={onCancelExistFile}
        confirmMessages={confirmMessages}
        okText={t('general_button_ok')}
        onOk={onOkExistFile}
        cancelText={t('general_button_cancel')}
        onCancel={onCancelExistFile}
      />
      <ConfirmModal
        isOpen={showConfirmUnPublic}
        toggle={onCancelAction}
        confirmMessage={
          documentState && documentState.documentDtails && !documentState.documentDtails.isFile
            ? t('folder_un_public_confirm')
            : t('document_un_public_confirm')
        }
        cancelText={t('general_discard')}
        okText={t('general_button_ok')}
        onCancel={onCancelAction}
        onOk={onOkUnPublic}
      />
    </>
  );
};

DocumentForm.defaultProps = {
  errorMessage: '',
  uploadedError: '',
  color: null,
  client: '',
  newTags: null,
  newDocs: null,
};
DocumentForm.propTypes = {
  showError: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  uploadedError: PropTypes.string,
  disbleDocument: PropTypes.bool.isRequired,
  color: PropTypes.string,
  client: PropTypes.string,
  onErrorLogoUploading: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  onToggleError: PropTypes.func.isRequired,
  onUpdateDocument: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  states: PropTypes.arrayOf(shape({})).isRequired,
  tags: PropTypes.arrayOf(shape({})).isRequired,
  newDocs: PropTypes.shape({
    current: PropTypes.string,
  }),
  newState: PropTypes.shape({
    current: PropTypes.string,
  }).isRequired,
  newTags: PropTypes.shape({}),
  loadingCreate: PropTypes.bool.isRequired,
  loadingUpdate: PropTypes.bool.isRequired,
  disableButton: PropTypes.bool.isRequired,
  loadingPublicDocument: PropTypes.bool.isRequired,
  setCurrentColor: PropTypes.func.isRequired,
  newPublic: PropTypes.shape({
    current: PropTypes.bool,
  }).isRequired,
  contacts: PropTypes.arrayOf(shape({})).isRequired,
};

export default DocumentForm;
